/**
 * src/pages/Dashboard.js
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [togglingAccount, setTogglingAccount] = useState(false);
  const [optimisticEnabled, setOptimisticEnabled] = useState(null);

  async function fetchData() {
    setError('');
    setLoading(true);
    try {
      const [accountRes, versionsRes] = await Promise.all([
        api.get('/portal/account'),
        api.get('/portal/config/versions')
      ]);
      setAccount(accountRes.data);
      setVersions(versionsRes.data);
    } catch (err) {
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response.data?.message || 'Failed to load dashboard data.');
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchData(); }, []);

  async function handleToggleAccount() {
    if (!account || togglingAccount) return;
    setError('');
    const newValue = !account.enabled;
    setOptimisticEnabled(newValue);  // flip immediately
    setTogglingAccount(true);
    try {
      const res = await api.put('/portal/account/enabled', { enabled: newValue });
      setAccount(prev => ({ ...prev, enabled: res.data.enabled }));
    } catch (err) {
      setOptimisticEnabled(null);  // revert on failure
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response.data?.message || 'Failed to update account status.');
      }
    } finally {
      setTogglingAccount(false);
      setOptimisticEnabled(null);
    }
  }

  // Active = published version with the most recent published_at timestamp
  const activeVersion = versions
    .filter(v => v.published_at)
    .sort((a, b) => new Date(b.published_at) - new Date(a.published_at))[0];
  const draftCount = versions.filter(v => !v.published_at).length;

  if (loading) return (
    <div className="page">
      <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>
    </div>
  );

  // Load failed with no data — show only the error, not misleading empty dashboard
  if (error && !account) return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Overview of your Process Tracker configuration</p>
      </div>
      <div className="alert alert-error">
        {error}
        <button className="alert-retry" onClick={fetchData}>Try again</button>
      </div>
    </div>
  );

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Overview of your Process Tracker configuration</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {activeVersion?.has_debug && (
        <div className="alert alert-warning" style={{ marginBottom: '20px' }}>
          The active config (v{activeVersion.version_number}) has debug mode enabled on one or more steps.
        </div>
      )}

      {/* First-run onboarding */}
      {user?.role === 'admin' && !versions.some(v => v.published_at) && (
        <div className="card" style={{ marginBottom: '24px', border: '1px solid var(--accent)', background: 'var(--accent-subtle)' }}>
          <div className="card-body">
            <h2 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
              Get started with Process Tracker
            </h2>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
              Set up tracking for your organization in three steps.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Step 1: Create config */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{
                  width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: '600',
                  background: versions.length > 0 ? 'var(--success)' : 'var(--accent)',
                  color: 'white'
                }}>
                  {versions.length > 0 ? '✓' : '1'}
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                    Create your tracking configuration
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    Define which processes, pages, and interactions to track.
                  </div>
                  {versions.length === 0 && (
                    <Link to="/config" className="btn btn-primary btn-sm" style={{ marginTop: '8px', display: 'inline-flex' }}>
                      Open Config Editor
                    </Link>
                  )}
                </div>
              </div>

              {/* Step 2: Publish */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{
                  width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: '600',
                  background: versions.length > 0 ? 'var(--accent)' : 'var(--border)',
                  color: versions.length > 0 ? 'white' : 'var(--text-tertiary)'
                }}>
                  2
                </div>
                <div>
                  <div style={{
                    fontSize: '14px', fontWeight: '500',
                    color: versions.length > 0 ? 'var(--text-primary)' : 'var(--text-tertiary)'
                  }}>
                    Publish your configuration
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    Publishing makes the config available to the Chrome extension.
                  </div>
                  {versions.length > 0 && (
                    <Link to="/config/history" className="btn btn-primary btn-sm" style={{ marginTop: '8px', display: 'inline-flex' }}>
                      Go to Version History
                    </Link>
                  )}
                </div>
              </div>

              {/* Step 3: Install extension */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{
                  width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: '600',
                  background: 'var(--border)',
                  color: 'var(--text-tertiary)'
                }}>
                  3
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-tertiary)' }}>
                    Install the Chrome extension
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    Install the extension on user browsers and configure it with your write API key.
                    Your keys are on the{' '}
                    <Link to="/api-keys" style={{ color: 'var(--accent)' }}>API Keys</Link> page.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status cards */}
      <div className="grid-3" style={{ marginBottom: '24px' }}>
        <div className="card">
          <div className="card-body">
            <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
              Tracking Status
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '20px', fontWeight: '600', color: account?.enabled ? 'var(--success)' : 'var(--danger)' }}>
                  {account?.enabled ? 'Active' : 'Disabled'}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                  {account?.enabled ? 'Events are being captured' : 'No events are being captured'}
                </div>
              </div>
              {user?.role === 'admin' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {togglingAccount && (
                    <div style={{
                      width: '14px', height: '14px',
                      border: '2px solid var(--border)',
                      borderTopColor: 'var(--accent)',
                      borderRadius: '50%',
                      animation: 'spin 0.6s linear infinite'
                    }} />
                  )}
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={optimisticEnabled !== null ? optimisticEnabled : (account?.enabled || false)}
                      onChange={handleToggleAccount}
                      disabled={togglingAccount}
                    />
                    <div className="toggle-track" />
                    <div className="toggle-thumb" />
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
              Active Config Version
            </div>
            <div style={{ fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)' }}>
              {activeVersion ? `v${activeVersion.version_number}` : '—'}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
              {activeVersion
                ? `Published ${new Date(activeVersion.published_at).toLocaleDateString()}`
                : 'No published config'}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
              Unpublished Drafts
            </div>
            <div style={{ fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)' }}>
              {draftCount}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
              {draftCount > 0
                ? <Link to="/config/history" style={{ color: 'var(--accent)' }}>View drafts →</Link>
                : 'No unpublished drafts'}
            </div>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Quick Actions</span>
        </div>
        <div className="card-body" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Link to="/config" className="btn btn-primary">
            Edit Configuration
          </Link>
          <Link to="/config/history" className="btn btn-secondary">
            View Version History
          </Link>
          {user?.role === 'admin' && (
            <Link to="/users" className="btn btn-secondary">
              Manage Users
            </Link>
          )}
        </div>
      </div>

      {/* Version history preview */}
      {versions.length > 0 && (
        <div className="card" style={{ marginTop: '24px' }}>
          <div className="card-header">
            <span className="card-title">Recent Versions</span>
            <Link to="/config/history" className="btn btn-ghost btn-sm">View all</Link>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Config</th>
                  <th>Created by</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {versions.slice(0, 5).map(v => {
                  const isActive = activeVersion?.id === v.id;
                  return (
                    <tr key={v.id} onClick={() => navigate(`/config/history/${v.id}`)}
                      style={{ cursor: 'pointer' }} title="Click to view this version">
                      <td><span className="font-mono" style={{ fontWeight: '500' }}>v{v.version_number}</span></td>
                      <td>
                        {isActive
                          ? <span className="badge badge-success"><span className="status-dot" />Active</span>
                          : v.published_at
                          ? <span className="badge badge-neutral"><span className="status-dot" />Published</span>
                          : <span className="badge badge-warning"><span className="status-dot" />Draft</span>}
                        {v.has_debug && (
                          <span className="badge badge-warning" style={{ marginLeft: '6px' }}>
                            <span className="status-dot" />Debug
                          </span>
                        )}
                      </td>
                      <td>{v.process_count} proc / {v.total_step_count} steps</td>
                      <td>{v.created_by_email}</td>
                      <td className="td-mono">{new Date(v.created_at).toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}